import axios from 'axios';
import { createAsyncThunk, isFulfilled, isPending, isRejected, PayloadAction } from '@reduxjs/toolkit';

import { cleanEntity } from 'app/shared/util/entity-utils';
import { IQueryParams, createEntitySlice, EntityState, serializeAxiosError } from 'app/shared/reducers/reducer.utils';
import { IScheduling, defaultValue } from 'app/shared/model/scheduling.model';
import numeral from 'numeral';
import { DaysOfWeek } from 'app/shared/model/enumerations/daysOfWeek.model';
import { Status } from 'app/shared/model/enumerations/scheduling-status.model';

const initialState: EntityState<IScheduling> = {
  loading: false,
  errorMessage: null,
  entities: [],
  entitiesAlt: {},
  entity: defaultValue,
  updating: false,
  totalItems: 0,
  updateSuccess: false,
};

const apiUrl = 'api/schedulings';
interface ICountSchedules {
  day: string;
  length: number;
}

interface IResponsePayload {
  data: ICountSchedules[];
}
interface ISchedulingPartialState extends IQueryParams {
  licensePlate?: string;
  schedulingId?: number;
  userId?: number;
  description?: string;
  status?: Status;
  startDate?: string;
  monthDate?: string;
  endDate?: string;
  showCanceled?: boolean;
  userName?: string;
  companyId?: number;
}
// Actions

export const getEntities = createAsyncThunk(
  'scheduling/fetch_entity_list',
  async ({ page, size, sort, licensePlate, schedulingId }: ISchedulingPartialState) => {
    const schedulingIdRequest = schedulingId ? `schedulingId=${schedulingId}&` : '';
    const requestUrl = `${apiUrl}${
      sort ? `?page=${page}&size=${size}&sort=${sort}&plateSearch=${licensePlate}&${schedulingIdRequest}` : '?'
    }cacheBuster=${new Date().getTime()}`;
    return axios.get<IScheduling[]>(requestUrl);
  }
);

export const getEntitiesByUserId = createAsyncThunk('scheduling/fetch_entity_list', async ({ page, size, sort, userId }: ISchedulingPartialState) => {
  const requestUrl = `${apiUrl}/userSchedulings?userId=${userId}`;
  return axios.get<IScheduling[]>(requestUrl);
});

export const getCountSchedulesByDay = createAsyncThunk(
  'scheduling/count_entity_by_date',
  async ({ startDate, endDate }: ISchedulingPartialState) => {
    const startDateFilter = startDate ? `startDate=${startDate}&` : '';
    const endDateFilter = endDate ? `endDate=${endDate}&` : '';
    const requestUrl = `${apiUrl}/date/count-by-day?${startDateFilter}${endDateFilter}`;
    const result = await axios.get<IResponsePayload>(requestUrl);
    return result;
  },
  { serializeError: serializeAxiosError }
);

export const getEntitiesByDate = createAsyncThunk(
  'scheduling/fetch_entity_list_by_date',
  async ({ page, size, sort, licensePlate, description, status, userName, startDate, monthDate, companyId }: ISchedulingPartialState) => {
    const statusFilter = status ? `status=${status}&` : '';
    const startDateFilter = startDate ? `startDate=${startDate}&` : '';
    const monthDateFilter = monthDate ? `monthDate=${monthDate}&` : '';
    const licensePlateFilter = licensePlate ? `licensePlate=${licensePlate}&` : '';
    const descriptionFilter = description ? `description=${description}&` : '';
    const companyIdFilter = companyId ? `companyId=${companyId}&` : '';
    const userNameFilter = userName ? `userName=${userName}&` : '';
    const requestUrl = `${apiUrl}/date/filter?page=${page}&size=${size}&sort=${sort}&${licensePlateFilter}${companyIdFilter}${descriptionFilter}${statusFilter}${startDateFilter}${monthDateFilter}${userNameFilter}cacheBuster=${new Date().getTime()}`;
    return axios.get<IScheduling[]>(requestUrl);
  }
);
export const getEntity = createAsyncThunk(
  'scheduling/fetch_entity',
  async (id: string | number) => {
    const requestUrl = `${apiUrl}/${id}`;
    return axios.get<IScheduling>(requestUrl);
  },
  { serializeError: serializeAxiosError }
);

export const createEntity = createAsyncThunk(
  'scheduling/create_entity',
  async (entity: IScheduling, thunkAPI) => {
    const result = await axios.post<IScheduling>(apiUrl, cleanEntity(entity));
    thunkAPI.dispatch(getEntities({}));
    return result;
  },
  { serializeError: serializeAxiosError }
);

interface ISchedulingQueryParams extends IQueryParams {
  entity: IScheduling;
  daysOfWeek: DaysOfWeek[];
  numberOfWeek: number;
}

export const createRecurringScheduling = createAsyncThunk(
  'scheduling/create_recurring_scheduling',
  async ({ entity, daysOfWeek, numberOfWeek }: ISchedulingQueryParams, thunkAPI) => {
    const requestUrl = `${apiUrl}/recurring?daysOfWeek=${daysOfWeek.join(',')}&numberOfWeek=${numberOfWeek}`;
    const result = await axios.post<IScheduling>(requestUrl, cleanEntity(entity));
    thunkAPI.dispatch(getEntities({}));
    return result;
  },
  { serializeError: serializeAxiosError }
);

export const updateEntity = createAsyncThunk(
  'scheduling/update_entity',
  async (entity: IScheduling, thunkAPI) => {
    const result = await axios.put<IScheduling>(`${apiUrl}/${entity.id}`, cleanEntity(entity));
    thunkAPI.dispatch(getEntities({}));
    return result;
  },
  { serializeError: serializeAxiosError }
);

export const partialUpdateEntity = createAsyncThunk(
  'scheduling/partial_update_entity',
  async (entity: IScheduling, thunkAPI) => {
    const result = await axios.patch<IScheduling>(`${apiUrl}/${entity.id}`, cleanEntity(entity));
    thunkAPI.dispatch(getEntities({}));
    return result;
  },
  { serializeError: serializeAxiosError }
);

export const cancelEntity = createAsyncThunk(
  'scheduling/cancel_entity',
  async (entity: IScheduling, thunkAPI) => {
    const id = entity.id;
    const result = await axios.put<IScheduling>(`${apiUrl}/${id}/cancel`, cleanEntity(entity));
    thunkAPI.dispatch(getEntities({}));
    return result;
  },
  { serializeError: serializeAxiosError }
);

export const deleteEntity = createAsyncThunk(
  'scheduling/delete_entity',
  async (id: string | number, thunkAPI) => {
    const requestUrl = `${apiUrl}/${id}`;
    const result = await axios.delete<IScheduling>(requestUrl);
    thunkAPI.dispatch(getEntities({}));
    return result;
  },
  { serializeError: serializeAxiosError }
);

// slice

export const SchedulingSlice = createEntitySlice({
  name: 'scheduling',
  initialState,
  extraReducers(builder) {
    builder
      .addCase(getEntity.fulfilled, (state, action) => {
        state.loading = false;
        state.entity = action.payload.data;
      })
      .addCase(deleteEntity.fulfilled, state => {
        state.updating = false;
        state.updateSuccess = true;
        state.entity = {};
      })
      .addMatcher(isFulfilled(cancelEntity), (state, action) => {
        state.updating = false;
        state.loading = false;
        state.updateSuccess = true;
        state.entity = action.payload.data;
      })
      .addMatcher(isFulfilled(getEntitiesByDate), (state, action) => {
        return {
          ...state,
          loading: false,
          entities: action.payload.data,
          totalItems: parseInt(action.payload.headers['x-total-count'], 10),
        };
      })
      .addMatcher(isFulfilled(getEntities), (state, action) => {
        return {
          ...state,
          loading: false,
          entities: action.payload.data,
          totalItems: parseInt(action.payload.headers['x-total-count'], 10),
        };
      })
      .addMatcher(isFulfilled(getCountSchedulesByDay), (state, action) => {
        return {
          ...state,
          loading: false,
          entitiesAlt: action.payload.data,
          // totalItems: parseInt(action.payload.headers['x-total-count'], 10),
        };
      })
      .addMatcher(isFulfilled(getEntitiesByUserId), (state, action) => {
        return {
          ...state,
          loading: false,
          entities: action.payload.data,
          totalItems: parseInt(action.payload.headers['x-total-count'], 10),
        };
      })
      .addMatcher(isFulfilled(createEntity, updateEntity, partialUpdateEntity, createRecurringScheduling), (state, action) => {
        state.updating = false;
        state.loading = false;
        state.updateSuccess = true;
        state.entity = action.payload.data;
      })
      .addMatcher(isPending(getEntities, getEntity, getEntitiesByUserId, getEntitiesByDate), state => {
        state.errorMessage = null;
        state.updateSuccess = false;
        state.loading = true;
      })
      .addMatcher(isPending(createEntity, updateEntity, partialUpdateEntity, deleteEntity, createRecurringScheduling), state => {
        state.errorMessage = null;
        state.updateSuccess = false;
        state.updating = true;
      })
      .addMatcher(isPending(cancelEntity), state => {
        state.errorMessage = null;
        state.updateSuccess = false;
        state.updating = true;
      });
  },
});

export const { reset } = SchedulingSlice.actions;

// Reducer
export default SchedulingSlice.reducer;
